import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  buildMarketplaceCategoryLocation,
  buildMarketplaceFacetLocation,
  getMarketplaceRequestKeyword,
  getMarketplaceSearchPath,
  getMarketplaceSearchPlaceholder,
  isMarketplaceListPath,
  MARKETPLACE_CATEGORY_ALL,
  MARKETPLACE_KEYWORD_MIN_LENGTH,
  normalizeMarketplaceKeyword,
  readMarketplaceCategory,
  readMarketplaceContentTypes,
  readMarketplaceFollowingBrands,
  readMarketplaceKeyword,
  readMarketplacePlatforms,
  readMarketplaceSaved,
  setMarketplaceKeywordParam,
} from '../../utils/marketplace/marketplaceKeywordSearch';
import { pushMarketplaceKeywordHistory } from '../../utils/marketplace/marketplaceKeywordHistory';
import {
  buildBlogSearchLocation,
  isBlogPath,
  readBlogSearchQuery,
} from '../../utils/blog/blogSearch';

const emptyFacetsOffPath = () => ({
  category: MARKETPLACE_CATEGORY_ALL,
  platforms: [],
  contentTypes: [],
  saved: false,
  followingBrands: false,
});

const readFacetsFromLocation = (pathname, search) => {
  if (!isMarketplaceListPath(pathname)) {
    return emptyFacetsOffPath();
  }
  return {
    category: readMarketplaceCategory(search),
    platforms: readMarketplacePlatforms(search),
    contentTypes: readMarketplaceContentTypes(search),
    saved: readMarketplaceSaved(search),
    followingBrands: readMarketplaceFollowingBrands(search),
  };
};

/**
 * Shared draft + navigate wiring for Header and MobileBrandBar marketplace search.
 * submit() returns { navigated, reason } so chrome can keep the panel open on too-short queries.
 */
function useMarketplaceChromeSearch(userType, { onGuestAction } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [draft, setDraft] = useState(() => readMarketplaceKeyword(location.search));
  const [category, setCategoryState] = useState(() => (
    readFacetsFromLocation(location.pathname, location.search).category
  ));
  const [platforms, setPlatformsState] = useState(() => (
    readFacetsFromLocation(location.pathname, location.search).platforms
  ));
  const [contentTypes, setContentTypesState] = useState(() => (
    readFacetsFromLocation(location.pathname, location.search).contentTypes
  ));
  const [saved, setSavedState] = useState(() => (
    readFacetsFromLocation(location.pathname, location.search).saved
  ));
  const [followingBrands, setFollowingBrandsState] = useState(() => (
    readFacetsFromLocation(location.pathname, location.search).followingBrands
  ));

  useEffect(() => {
    if (location.pathname === '/blog') {
      setDraft(readBlogSearchQuery(location.search));
      return;
    }
    if (!isMarketplaceListPath(location.pathname)) return;
    setDraft(readMarketplaceKeyword(location.search));
    const next = readFacetsFromLocation(location.pathname, location.search);
    setCategoryState(next.category);
    setPlatformsState(next.platforms);
    setContentTypesState(next.contentTypes);
    setSavedState(next.saved);
    setFollowingBrandsState(next.followingBrands);
  }, [location.pathname, location.search]);

  const navigateTo = useCallback((next) => {
    if (next.pathname === location.pathname && next.search === location.search) {
      return false;
    }
    window.scrollTo(0, 0);
    // Hard refresh when entering a marketplace list from elsewhere so list
    // state (filters, results, chrome) remounts cleanly with the new URL.
    const enteringMarketplaceList = (
      isMarketplaceListPath(next.pathname)
      && !isMarketplaceListPath(location.pathname)
    );
    if (enteringMarketplaceList) {
      const href = `${next.pathname}${next.search || ''}`;
      window.location.assign(href);
      return true;
    }
    navigate({
      pathname: next.pathname,
      search: next.search,
    });
    return true;
  }, [location.pathname, location.search, navigate]);

  const rememberKeyword = useCallback((keyword) => {
    const requestKeyword = getMarketplaceRequestKeyword(keyword);
    if (!requestKeyword) return;
    pushMarketplaceKeywordHistory(userType, requestKeyword);
  }, [userType]);

  const activeKeyword = useCallback(() => {
    const keyword = getMarketplaceRequestKeyword(draft);
    if (keyword) {
      setDraft(keyword);
    }
    return keyword || '';
  }, [draft]);

  const isBlogSearch = isBlogPath(location.pathname);

  const submit = useCallback(() => {
    const normalized = normalizeMarketplaceKeyword(draft);
    setDraft(normalized);

    if (isBlogPath(location.pathname)) {
      if (!normalized) {
        const navigated = navigateTo(buildBlogSearchLocation(''));
        return { navigated, reason: 'browse' };
      }
      if (normalized.length < MARKETPLACE_KEYWORD_MIN_LENGTH) {
        return { navigated: false, reason: 'too-short' };
      }
      const navigated = navigateTo(buildBlogSearchLocation(normalized));
      return { navigated, reason: 'search' };
    }

    if (!normalized) {
      const navigated = navigateTo(buildMarketplaceCategoryLocation({
        pathname: location.pathname,
        search: location.search,
        userType,
        category,
        keyword: '',
      }));
      return { navigated, reason: 'browse' };
    }

    if (normalized.length < MARKETPLACE_KEYWORD_MIN_LENGTH) {
      return { navigated: false, reason: 'too-short' };
    }

    const navigated = navigateTo(buildMarketplaceCategoryLocation({
      pathname: location.pathname,
      search: location.search,
      userType,
      category,
      keyword: normalized,
    }));
    if (navigated) {
      rememberKeyword(normalized);
    }
    return { navigated, reason: 'search' };
  }, [category, draft, location.pathname, location.search, navigateTo, rememberKeyword, userType]);

  const clear = useCallback(() => {
    setDraft('');
    if (location.pathname === '/blog' && readBlogSearchQuery(location.search)) {
      navigate({
        pathname: '/blog',
        search: '',
        hash: location.hash,
      });
      return;
    }
    if (!isMarketplaceListPath(location.pathname)) return;
    const nextSearch = setMarketplaceKeywordParam(location.search, '');
    if (nextSearch === location.search) return;
    navigate({
      pathname: location.pathname,
      search: nextSearch,
      hash: location.hash,
    });
  }, [location.hash, location.pathname, location.search, navigate]);

  const selectCategory = useCallback((nextCategory) => {
    const resolved = String(nextCategory || '').trim() || MARKETPLACE_CATEGORY_ALL;
    setCategoryState(resolved);
    const keyword = activeKeyword();
    navigateTo(buildMarketplaceFacetLocation({
      pathname: location.pathname,
      search: location.search,
      userType,
      category: resolved,
      keyword,
    }));
  }, [activeKeyword, location.pathname, location.search, navigateTo, userType]);

  const selectPlatform = useCallback((nextPlatform) => {
    const resolved = String(nextPlatform || '').trim();
    const nextPlatforms = resolved ? [resolved] : [];
    setPlatformsState(nextPlatforms);
    const keyword = activeKeyword();
    navigateTo(buildMarketplaceFacetLocation({
      pathname: location.pathname,
      search: location.search,
      userType,
      platforms: nextPlatforms,
      keyword,
    }));
  }, [activeKeyword, location.pathname, location.search, navigateTo, userType]);

  const selectContentFormat = useCallback((nextFormat) => {
    const resolved = String(nextFormat || '').trim();
    const nextContentTypes = resolved ? [resolved] : [];
    setContentTypesState(nextContentTypes);
    const keyword = activeKeyword();
    navigateTo(buildMarketplaceFacetLocation({
      pathname: location.pathname,
      search: location.search,
      userType,
      contentTypes: nextContentTypes,
      keyword,
    }));
  }, [activeKeyword, location.pathname, location.search, navigateTo, userType]);

  const clearFacets = useCallback(() => {
    setCategoryState(MARKETPLACE_CATEGORY_ALL);
    setPlatformsState([]);
    setContentTypesState([]);
    setSavedState(false);
    setFollowingBrandsState(false);
    const keyword = activeKeyword();
    navigateTo(buildMarketplaceFacetLocation({
      pathname: location.pathname,
      search: location.search,
      userType,
      clearFacets: true,
      keyword,
    }));
  }, [activeKeyword, location.pathname, location.search, navigateTo, userType]);

  const selectSaved = useCallback(() => {
    if (!userType) {
      const intent = location.pathname.startsWith('/marketplace/campaigns')
        ? 'saveCampaign'
        : 'saveCreator';
      onGuestAction?.(intent);
      return;
    }

    const keyword = activeKeyword();
    if (userType === 'influencer') {
      const targetPath = getMarketplaceSearchPath('influencer');
      const currentlyOn = location.pathname === targetPath && readMarketplaceSaved(location.search);
      const nextSaved = !currentlyOn;
      setSavedState(nextSaved);
      navigateTo(buildMarketplaceFacetLocation({
        pathname: location.pathname,
        search: location.search,
        userType,
        saved: nextSaved,
        keyword,
      }));
      return;
    }

    // Brands: Saved = favorited creators.
    const targetPath = getMarketplaceSearchPath('brand');
    const currentlyOn = location.pathname === targetPath
      && readMarketplaceFollowingBrands(location.search);
    const nextFollowing = !currentlyOn;
    setFollowingBrandsState(nextFollowing);
    navigateTo(buildMarketplaceFacetLocation({
      pathname: location.pathname,
      search: location.search,
      userType: 'brand',
      followingBrands: nextFollowing,
      keyword,
    }));
  }, [
    activeKeyword,
    location.pathname,
    location.search,
    navigateTo,
    onGuestAction,
    userType,
  ]);

  const selectBrandsYouFollow = useCallback(() => {
    const keyword = activeKeyword();
    const targetPath = getMarketplaceSearchPath('influencer');
    const currentlyOn = location.pathname === targetPath
      && readMarketplaceFollowingBrands(location.search);
    const nextFollowing = !currentlyOn;
    setFollowingBrandsState(nextFollowing);
    navigateTo(buildMarketplaceFacetLocation({
      pathname: location.pathname,
      search: location.search,
      userType: 'influencer',
      followingBrands: nextFollowing,
      keyword,
    }));
  }, [
    activeKeyword,
    location.pathname,
    location.search,
    navigateTo,
  ]);

  const selectSuggestion = useCallback((item) => {
    if (!item) return;

    if (isBlogPath(location.pathname)) {
      const keyword = getMarketplaceRequestKeyword(item.keyword || item.label || '');
      if (!keyword) return;
      setDraft(keyword);
      navigateTo(buildBlogSearchLocation(keyword));
      return;
    }

    if (item.kind === 'popular' || item.kind === 'category') {
      setCategoryState(item.category || MARKETPLACE_CATEGORY_ALL);
      navigateTo(buildMarketplaceCategoryLocation({
        pathname: location.pathname,
        search: location.search,
        userType,
        category: item.category,
        keyword: '',
      }));
      return;
    }

    if (item.kind === 'category-scope') {
      const keyword = getMarketplaceRequestKeyword(item.keyword || draft);
      if (keyword) {
        setDraft(keyword);
        rememberKeyword(keyword);
      } else {
        setDraft(normalizeMarketplaceKeyword(item.keyword || draft));
      }
      setCategoryState(item.category || MARKETPLACE_CATEGORY_ALL);
      navigateTo(buildMarketplaceCategoryLocation({
        pathname: location.pathname,
        search: location.search,
        userType,
        category: item.category,
        keyword,
      }));
      return;
    }

    // phrase | recent | free-text — keep the active category scope
    const keyword = getMarketplaceRequestKeyword(item.keyword || item.label || '');
    if (!keyword) {
      return;
    }
    setDraft(keyword);
    const navigated = navigateTo(buildMarketplaceCategoryLocation({
      pathname: location.pathname,
      search: location.search,
      userType,
      category,
      keyword,
    }));
    if (navigated) {
      rememberKeyword(keyword);
    }
  }, [category, draft, location.pathname, location.search, navigateTo, rememberKeyword, userType]);

  return {
    draft,
    setDraft,
    category,
    platforms,
    contentTypes,
    saved,
    followingBrands,
    selectCategory,
    selectPlatform,
    selectContentFormat,
    selectSaved,
    selectBrandsYouFollow,
    clearFacets,
    submit,
    clear,
    selectSuggestion,
    placeholder: isBlogSearch ? 'Search articles' : getMarketplaceSearchPlaceholder(),
    hideMarketplaceFacets: isBlogSearch,
  };
}

export default useMarketplaceChromeSearch;
